import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Typography } from "@material-ui/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import qs from "qs";
import { useHistory, useLocation } from "react-router";
import auth0Client from "app/auth/Auth";
import ModalContext from "../../../infrastructure/Contexts/components/ModalWrapper/context";
import {
  translate,
  translate as t,
} from "../../../infrastructure/translate/localization";
import fetchBenefitPlans from "../../../services/benefitPlan/fetchBenefitPlans";
import { showErrorMessage } from "../../../usecases/commons/show-error-message";
import CobeeButton from "../../components/CobeeButton";
import EmployeesContext from "../../../infrastructure/Contexts/components/data/EmployeesWrapper/context";
import BenefitPlansContext from "../../../infrastructure/Contexts/components/data/BenefitPlanWrapper/context";
import EmployeeCategoryTable from "./components/EmployeeCategoryTable";
import EmployeeDetailsModal from "../../components/modals/employee/EmployeeDetailsModal";
import useStyles from "./styles";
import EmployeeActionsContext from "../../../infrastructure/Contexts/components/actions/EmployeeActionsWrapper/context";
import { EmployeeNotRemoved } from "../../../infrastructure/Contexts/components/data/EmployeesWrapper/types";
import WrappedSidebarV2 from "../../../v2/ui/components/SidebarV2";

export default function UploadEmployeesScreen() {
  const { buttonBar, buttonText } = useStyles();
  const { search } = useLocation();
  const params = qs.parse(search, { ignoreQueryPrefix: true });
  const history = useHistory();

  const { employeesNotRemoved, isLoading: employeesAreLoading } =
    useContext(EmployeesContext);
  const { benefitPlans, setBenefitPlans } = useContext(BenefitPlansContext);
  const { openModal } = useContext(ModalContext);
  const {
    uploadMultipleEmployeesIsLoading,
    inviteUploadingEmployeesIsLoading,
  } = useContext(EmployeeActionsContext);

  useEffect(() => {
    const loadBenefitModels = async () => {
      try {
        await fetchBenefitPlans({ callback: setBenefitPlans });
      } catch (e) {
        showErrorMessage(translate("common-messages.basic-error-message"));
      }
    };
    loadBenefitModels();
  }, [setBenefitPlans]);

  const [categorizedEmployees, setCategorizedEmployees] = useState<{
    uploading: EmployeeNotRemoved[];
    error: EmployeeNotRemoved[];
  }>({
    uploading: [],
    error: [],
  });

  const tableData = useCallback(() => {
    return employeesNotRemoved.map(
      ({ modelId, ...rest }) =>
        new EmployeeNotRemoved({
          ...rest,
          benefitPlanName: benefitPlans.find(({ id }) => id === modelId)?.name,
          modelId,
        })
    );
  }, [employeesNotRemoved, benefitPlans]);

  useEffect(() => {
    const data = tableData();
    setCategorizedEmployees({
      uploading: data
        .filter(({ state, error }) => state === "uploading" && !error)
        .sort((a, b) => (a.warning < b.warning ? 1 : -1)),
      error: data.filter(({ state, error }) => state === "uploading" && error),
    });
  }, [tableData, setCategorizedEmployees]);

  const handleOpenAddEmployee = () => {
    openModal({
      title: t("upload-employees-section.add-employee"),
      modalContents: <EmployeeDetailsModal />,
    });
  };

  useEffect(() => {
    if (params.open === "true") {
      handleOpenAddEmployee();
      history.replace({
        search: "",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyId = auth0Client.getCompanyId();

  return (
    <Box pl="10rem">
      <WrappedSidebarV2 />
      <Box padding="2.5rem">
        <Box display="flex" justifyContent="space-between" mb={3}>
          <Typography variant="h5">
            {t("upload-employees-section.upload-employees")}
          </Typography>
          <Box display="flex" alignItems="center" className={buttonBar}>
            <CobeeButton styleType="secondary" onClick={handleOpenAddEmployee}>
              <FontAwesomeIcon icon="plus" />
              <Typography variant="body2" className={buttonText}>
                {t("upload-employees-section.add-employee")}
              </Typography>
            </CobeeButton>
          </Box>
        </Box>
        <Box bgcolor="white" padding={3}>
          <EmployeeCategoryTable
            isLoading={
              employeesAreLoading ||
              uploadMultipleEmployeesIsLoading ||
              inviteUploadingEmployeesIsLoading
            }
            category="error"
            data={categorizedEmployees.error}
          />
          <Box mb={categorizedEmployees.error.length ? 8 : 0} />
          <EmployeeCategoryTable
            isLoading={
              employeesAreLoading ||
              uploadMultipleEmployeesIsLoading ||
              inviteUploadingEmployeesIsLoading
            }
            category="uploading"
            data={categorizedEmployees.uploading}
          />
        </Box>
      </Box>
    </Box>
  );
}
